// Bump this to invalidate old sessionStorage entries.
const CACHE_VERSION = 3;
const CACHE_PREFIX = `bv:home-college:v${CACHE_VERSION}:`;
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const inMemory = new Map();

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const sessionKey = (key) => `${CACHE_PREFIX}${key}`;

export const clearCachedList = (key) => {
  inMemory.delete(key);
  try {
    window.sessionStorage?.removeItem(sessionKey(key));
  } catch {
    // ignore
  }
};

export const clearHomeCollegeListsCache = () => {
  clearCachedList("top");
  clearCachedList("private");
  clearCachedList("featured");
};

export const readCachedList = (key, ttlMs = DEFAULT_TTL_MS) => {
  const now = Date.now();

  const mem = inMemory.get(key);
  if (mem?.data && mem.ts && now - mem.ts < ttlMs) return mem.data;

  const raw = typeof window !== "undefined" ? window.sessionStorage?.getItem(sessionKey(key)) : null;
  if (!raw) return null;

  const parsed = safeParse(raw);
  if (!parsed || !parsed.ts || !Array.isArray(parsed.data)) return null;
  if (now - parsed.ts >= ttlMs) return null;

  inMemory.set(key, { ts: parsed.ts, data: parsed.data });
  return parsed.data;
};

export const writeCachedList = (key, data) => {
  if (!Array.isArray(data)) return;
  const entry = { ts: Date.now(), data };
  inMemory.set(key, entry);
  try {
    window.sessionStorage?.setItem(sessionKey(key), JSON.stringify(entry));
  } catch {
    // ignore storage failures (private mode / quota)
  }
};

export const fetchListDeduped = async (key, url, fetchOptions, ttlMs = DEFAULT_TTL_MS) => {
  const cached = readCachedList(key, ttlMs);
  if (cached) return cached;

  const existing = inMemory.get(key);
  if (existing?.promise) return existing.promise;

  const promise = fetch(url, fetchOptions)
    .then(async (res) => {
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : [];
      writeCachedList(key, list);
      return list;
    })
    .catch(() => {
      // If network fails, fall back to whatever we might have.
      return readCachedList(key, ttlMs) || [];
    })
    .finally(() => {
      const current = inMemory.get(key);
      if (current?.promise) {
        inMemory.set(key, { ts: current.ts, data: current.data });
      }
    });

  inMemory.set(key, { promise });
  return promise;
};

export const preloadHomeCollegeLists = ({ apiUrl, headers }) => {
  const options = { method: "POST", headers };
  // Fire-and-forget prefetch. Components will reuse the same in-flight promise.
  fetchListDeduped("top", `${apiUrl}/api/v1/college/top/list?limit=30`, options);
  fetchListDeduped("private", `${apiUrl}/api/v1/college/private/list?limit=48`, options);
  fetchListDeduped("featured", `${apiUrl}/api/v1/college/featured/list?limit=48`, options);
};
